import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  Button,
  Stack,
  Flex,
  useTheme,
} from '@fti/design-system';

interface PasswordConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmLabel: string;
  password?: string;
  loading?: boolean;
}

export const PasswordConfirmationModal: React.FC<PasswordConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  password = 'stratcomm',
  loading = false,
}) => {
  const { theme } = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  // Focus the input when modal opens
  useEffect(() => {
    if (isOpen) {
      // Delay to let the modal render and focus trap settle
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // Reset when closing
      if (inputRef.current) inputRef.current.value = '';
      setError(null);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    const value = inputRef.current?.value || '';
    if (value !== password) {
      setError('Incorrect password');
      inputRef.current?.focus();
      return;
    }
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
    onConfirm();
  };

  const handleClose = () => {
    if (inputRef.current) inputRef.current.value = '';
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="sm">
      <Modal.Header onClose={handleClose}>{title}</Modal.Header>
      <Modal.Body>
        <Stack gap="md">
          <p style={{ margin: 0, fontSize: '0.85rem' }}>{description}</p>
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.85rem',
              fontWeight: 500,
              marginBottom: '4px',
              color: theme.colors.text.primary,
            }}>
              Enter password to confirm
            </label>
            <input
              ref={inputRef}
              type="password"
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '0.875rem',
                borderRadius: '6px',
                border: `1px solid ${error ? theme.colors.semantic.error.default : theme.colors.border.default}`,
                background: theme.colors.background.default,
                color: theme.colors.text.primary,
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = error
                  ? theme.colors.semantic.error.default
                  : theme.colors.primary[500];
              }}
              onBlur={(e) => {
                e.target.style.borderColor = error
                  ? theme.colors.semantic.error.default
                  : theme.colors.border.default;
              }}
              onChange={() => {
                if (error) setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleConfirm();
              }}
            />
            {error && (
              <p style={{
                margin: '4px 0 0',
                fontSize: '0.8rem',
                color: theme.colors.semantic.error.default,
              }}>
                {error}
              </p>
            )}
          </div>
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Flex justify="end" gap="sm">
          <Button variant="outline" size="sm" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="solid" color="error" size="sm" onClick={handleConfirm} disabled={loading}>
            {loading ? 'Processing...' : confirmLabel}
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
};
